import { useState } from 'react';
import { useLog, clearLog, type LogEntry } from '../lib/log';
import './routes.css';

function fmtTs(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${String(
    d.getMilliseconds()
  ).padStart(3, '0')}`;
}

function entriesAsText(entries: LogEntry[]): string {
  return entries
    .map((e) => `[${fmtTs(e.ts)}] ${e.level.toUpperCase()} ${e.text}`)
    .join('\n');
}

function copyViaTextarea(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  ta.style.left = '0';
  ta.style.top = '0';
  document.body.appendChild(ta);
  try {
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    return ok;
  } catch {
    return false;
  } finally {
    document.body.removeChild(ta);
  }
}

export default function LogRoute() {
  const entries = useLog();
  const [status, setStatus] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function copy() {
    const text = entriesAsText(entries);
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) ok = copyViaTextarea(text);
    setStatus(ok ? 'copied' : 'failed');
    setTimeout(() => setStatus('idle'), 1800);
  }

  function onClear() {
    if (!confirm('Clear the log?')) return;
    clearLog();
  }

  const buttonLabel =
    status === 'copied' ? 'Copied!' : status === 'failed' ? 'Copy failed' : 'Copy all';

  return (
    <div className="route">
      <h1>Log</h1>
      <div className="dim small">
        Last {entries.length} entries (max 300). Captured from console.* and uncaught
        errors. Persists across reloads.
      </div>

      <div className="row">
        <button onClick={copy} disabled={entries.length === 0}>
          {buttonLabel}
        </button>
        <button className="ghost" onClick={onClear} disabled={entries.length === 0}>
          Clear
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="dim">No log entries.</div>
      ) : (
        <div className="log-list">
          {entries.map((e, i) => (
            <div key={i} className={`log-entry log-${e.level}`}>
              <span className="log-ts">{fmtTs(e.ts)}</span>
              <span className="log-level">{e.level}</span>
              <pre className="log-text">{e.text}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
